/**
 * <recommendations-feed> — a single infinite-scroll product grid whose items are
 * sourced, in priority order, from three criteria and merged/de-duped into one
 * feed. Configured by the JSON blob emitted in sections/featured-collection.liquid.
 *
 * Sourcing order (a source is skipped once exhausted):
 *   1. algorithm    — Storefront productRecommendations(RELATED). Fixed set (~10).
 *   2. categories   — best-sellers in the shopper's purchased categories
 *                     (product_type), falling back to the current product's type,
 *                     then store-wide. Cursor-paginated.
 *   3. bestsellers  — store-wide best-sellers, preferring those whose discount
 *                     state matches the current product's (discounted PDP →
 *                     discounted picks, else full-price); the non-matching ones
 *                     are held back and used once the source runs out of pages.
 *                     Cursor-paginated.
 *
 * Every candidate must: be available, have total stock >= config.minStock, not be
 * in the exclusion list (by handle or SKU), not be the current product, and not
 * already shown. A product is "discounted" when its compare-at price differs from
 * its price.
 *
 * Cards are rendered in JS reusing the theme's card class contract, so the global
 * quick-add (quick-add-cart.js) and wishlist (wishlist.js) delegated handlers bind
 * to them automatically — no per-card wiring here.
 *
 * The Storefront token, shop URL, country and language come from window.config /
 * window.shopUrl (head-init.liquid), which is already prod/dev- and market-aware.
 */
(function () {
  const PLUS_ICON =
    '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<rect width="24" height="24" fill="white" fill-opacity="0.6"/>' +
    '<path d="M12.0013 6.4746V18.1413M6.168 12.3079H17.8346" stroke="#2A2A2A" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const CART_ICON =
    '<svg class="svg-icon wishlist-cart" width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M8 6.63889C8 7.3756 8.29266 8.08214 8.81359 8.60307C9.33453 9.12401 10.0411 9.41667 10.7778 9.41667C11.5145 9.41667 12.221 9.12401 12.742 8.60307C13.2629 8.08214 13.5556 7.3756 13.5556 6.63889M3.625 4H17.375C17.7202 4 18 4.33164 18 4.74074V16.5926C18 17.0017 17.7202 17.3333 17.375 17.3333H3.625C3.27982 17.3333 3 17.0017 3 16.5926V4.74074C3 4.33164 3.27982 4 3.625 4Z" stroke="#2A2A2A" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // Mirrors {% render 'icons', name: 'clock' %} — the "last units" marker.
  const CLOCK_ICON =
    '<svg class="svg-icon variant-elem__clock" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="true" focusable="false">' +
    '<path d="M8.00002 13.5999C11.0928 13.5999 13.6 11.0927 13.6 7.99988C13.6 4.90709 11.0928 2.39988 8.00002 2.39988C4.90723 2.39988 2.40002 4.90709 2.40002 7.99988C2.40002 11.0927 4.90723 13.5999 8.00002 13.5999Z" stroke="currentColor" stroke-miterlimit="10"/>' +
    '<path d="M8 4.49992V7.99992H11.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const HEART_ICON =
    '<svg class="svg-icon" width="24" height="24" viewBox="0 0 24 24" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="true" focusable="false">' +
    '<path d="M12.0001 20.1999C12.0001 20.1999 3.6001 15.2999 3.6001 9.34993C3.6001 8.29808 3.94995 7.27872 4.59014 6.46528C5.23033 5.65184 6.1213 5.09458 7.11146 4.88829C8.10163 4.68201 9.12982 4.83945 10.0211 5.33383C10.9124 5.82821 11.6117 6.629 12.0001 7.59993C12.3885 6.629 13.0878 5.82821 13.9791 5.33383C14.8704 4.83945 15.8986 4.68201 16.8887 4.88829C17.8789 5.09458 18.7699 5.65184 19.4101 6.46528C20.0502 7.27872 20.4001 8.29808 20.4001 9.34993C20.4001 15.2999 12.0001 20.1999 12.0001 20.1999Z" stroke-width="0.9"/></svg>';

  // Shopify taxonomy categories whose products never belong in this feed —
  // gift bags are store packaging, not something to recommend. Matched by ID and
  // not by category.name on purpose: the Storefront API returns that name
  // TRANSLATED per market ("Gift Bags" in EN, "Bolsas de regalo" in ES/FR/IT),
  // so a name check would only ever fire in one language. Descendants of a
  // listed category are excluded too (see isExcludedCategory).
  const EXCLUDED_CATEGORY_IDS = [
    'gid://shopify/TaxonomyCategory/ae-3-1-5-1', // ... > Gift Giving > Gift Wrapping > Gift Bags
  ];

  const isExcludedCategory = (id) =>
    !!id && EXCLUDED_CATEGORY_IDS.some((base) => id === base || id.startsWith(base + '-'));

  // Individual products that must never appear in the feed, by numeric product
  // ID (the tail of the Storefront `gid://shopify/Product/<id>`). IDs are stable
  // across locales/markets, unlike the handle, which is translated.
  const EXCLUDED_PRODUCT_IDS = ['15566245134667'];

  const isExcludedProduct = (gid) =>
    !!gid && EXCLUDED_PRODUCT_IDS.includes(String(gid).split('/').pop());

  const PRODUCT_FIELDS = `
    id
    handle
    title
    productType
    category { id }
    availableForSale
    featuredImage { url altText width height }
    priceRange { minVariantPrice { amount currencyCode } }
    compareAtPriceRange { minVariantPrice { amount } }
    variants(first: 20) { nodes { id title availableForSale quantityAvailable selectedOptions { name value } } }
  `;

  // Variants in this store are color+size (e.g. "Rojo / XS"), so the size must be
  // read from selectedOptions, not the variant title.
  const SIZE_RE = /talla|size|taille|taglia|maat|tamanho|gr(ö|oe|o)sse/i;
  const sizeOf = (v) => {
    const opts = v.selectedOptions || [];
    let o = null;
    if (window.OptionTypeUtils && typeof window.OptionTypeUtils.isSize === 'function') {
      o = opts.find((x) => window.OptionTypeUtils.isSize(x.name));
    }
    if (!o) o = opts.find((x) => SIZE_RE.test(x.name));
    return o ? o.value : v.title;
  };

  const FEED_QUERY = `
    query Feed($first: Int!, $after: String, $q: String!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
      products(first: $first, after: $after, sortKey: BEST_SELLING, query: $q) {
        edges { cursor node { ${PRODUCT_FIELDS} } }
        pageInfo { hasNextPage }
      }
    }`;

  const REC_QUERY = `
    query Rec($productId: ID!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
      productRecommendations(productId: $productId, intent: RELATED) { ${PRODUCT_FIELDS} }
    }`;

  const NODES_QUERY = `
    query Nodes($ids: [ID!]!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
      nodes(ids: $ids) { ... on Product { ${PRODUCT_FIELDS} } }
    }`;

  const numericId = (gid) => (gid ? String(gid).split('/').pop() : '');
  const esc = (s) => String(s == null ? '' : s).replace(/"/g, '&quot;').replace(/</g, '&lt;');

  class RecommendationsFeed extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound) return;
      this.dataset.bound = 'true';

      const cfgEl = document.getElementById(this.dataset.config);
      if (!cfgEl) return;
      try {
        this.config = JSON.parse(cfgEl.textContent);
      } catch (e) {
        console.warn('[recs] bad config', e);
        return;
      }

      const glob = window.config || {};
      this.token = glob.storefrontAccessToken;
      this.shopUrl = window.shopUrl || window.location.origin;
      this.country = glob.countryIsoCode || undefined;
      // LanguageCode enum values are upper-cased with underscores (e.g. PT_BR),
      // while localization.language.iso_code is lower-cased with hyphens (e.g. pt-br).
      this.language = glob.lang ? String(glob.lang).replace(/-/g, '_').toUpperCase() : undefined;
      this.endpoint = `${this.shopUrl}/api/${this.config.apiVersion || '2024-10'}/graphql.json`;

      this.grid = this.querySelector('[data-recs-grid]');
      this.sentinel = this.querySelector('[data-recs-sentinel]');
      this.status = this.querySelector('[data-recs-status]');

      this.sources = (this.config.sources || []).filter(Boolean);
      this.currentHandle = (this.config.handle || '').toLowerCase();
      this.minStock = Number(this.config.minStock) || 0;
      this.batchSize = Math.max(2, (Number(this.config.colsDesktop) || 4) * (Number(this.config.rowsPerBatch) || 3));
      this.purchasedGids = (this.config.purchasedComplementary || []).filter(Boolean);

      this.seen = new Set();
      this.excluded = this.parseExclusions(this.config.excludedRaw);
      this.srcIndex = 0;
      this.exhausted = {};
      this.algo = { loaded: false, queue: [] };
      this.purchased = { loaded: false, queue: [] };
      this.cat = { cursor: null, done: false, spill: [] };
      this.best = { cursor: null, done: false, spill: [] };
      this.loading = false;
      this.done = false;
      this.emptyRetries = 0;

      if (!this.token || !this.grid || !this.sources.length) {
        this.finish();
        return;
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) this.loadNext();
          },
          { rootMargin: '600px 0px' }
        );
        this.observer.observe(this.sentinel);
      }

      this.loadNext();
    }

    // --- helpers -------------------------------------------------------------
    parseExclusions(raw) {
      const set = new Set();
      if (!raw) return set;
      String(raw)
        .split(/[\n,;]+/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
        .forEach((s) => set.add(s));
      return set;
    }

    async gql(query, variables) {
      const res = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.token,
        },
        body: JSON.stringify({ query, variables }),
      });
      const json = await res.json();
      if (json.errors) console.warn('[recs] gql errors', json.errors);
      return json.data;
    }

    isDiscounted(p) {
      const price = parseFloat(p?.priceRange?.minVariantPrice?.amount || '0');
      const cmp = parseFloat(p?.compareAtPriceRange?.minVariantPrice?.amount || '0');
      return cmp > 0 && Math.abs(cmp - price) > 0.001;
    }

    // Predicate + de-dupe. Mutates `seen` on success, so only call when the
    // product will actually be rendered.
    accept(p) {
      if (!p || !p.handle) return false;
      const handle = p.handle.toLowerCase();
      if (this.seen.has(handle)) return false;
      if (this.currentHandle && handle === this.currentHandle) return false;
      if (this.config.productGid && p.id === this.config.productGid) return false;
      if (!p.availableForSale) return false;

      if (this.excluded.size && this.excluded.has(handle)) return false;
      if (isExcludedProduct(p.id)) return false;
      if (isExcludedCategory(p.category?.id)) return false;

      if (this.minStock > 0) {
        const total = (p.variants?.nodes || []).reduce(
          (sum, v) => sum + (Number.isFinite(v.quantityAvailable) ? Math.max(0, v.quantityAvailable) : 0),
          0
        );
        if (total < this.minStock) return false;
      }

      this.seen.add(handle);
      return true;
    }

    // NOTE: `available_for_sale:true` is deliberately absent from the search
    // string. ANDing it with product_type returns zero hits against the
    // Storefront search index, and on its own it only matches a small slice of
    // the catalog. Availability is enforced client-side in accept() instead.
    catQueryString() {
      if (this._catQuery != null) return this._catQuery;
      let types = (this.config.purchasedTypes || []).map((t) => (t || '').trim()).filter(Boolean);
      if (!types.length && this.config.currentType) types = [this.config.currentType];
      const clause = types.map((t) => `product_type:${JSON.stringify(t)}`).join(' OR ');
      this._catQuery = types.length ? `(${clause})` : '';
      return this._catQuery;
    }

    // --- sourcing ------------------------------------------------------------
    async fillBatch(target) {
      const out = [];
      let guard = 0;
      while (out.length < target && this.srcIndex < this.sources.length && guard < 40) {
        guard++;
        const src = this.sources[this.srcIndex];
        const want = target - out.length;
        let got = [];
        if (src === 'algorithm') got = await this.pullAlgorithm(want);
        else if (src === 'purchased') got = await this.pullPurchased(want);
        else if (src === 'categories') got = await this.pullQuery('categories', this.cat, this.catQueryString(), want, false);
        else if (src === 'bestsellers') got = await this.pullQuery('bestsellers', this.best, '', want, true);
        else this.exhausted[src] = true;

        out.push(...got);
        if (this.exhausted[src]) {
          this.srcIndex++;
          continue;
        }
        // Page cap reached without exhausting the source: stop this batch and
        // resume on the next scroll trigger.
        if (!got.length) break;
      }
      return out;
    }

    async pullAlgorithm(want) {
      if (!this.algo.loaded) {
        this.algo.loaded = true;
        if (this.config.productGid) {
          const data = await this.gql(REC_QUERY, {
            productId: this.config.productGid,
            country: this.country,
            language: this.language,
          });
          this.algo.queue = (data && data.productRecommendations) || [];
        }
      }
      const out = [];
      while (out.length < want && this.algo.queue.length) {
        const p = this.algo.queue.shift();
        if (this.accept(p)) out.push(p);
      }
      if (!this.algo.queue.length) this.exhausted.algorithm = true;
      return out;
    }

    // Complementary-product recommendations of the shopper's past-ordered
    // products. GIDs are resolved server-side in Liquid; here we batch-fetch
    // their card data by id (one shot, order preserved).
    async pullPurchased(want) {
      if (!this.purchased.loaded) {
        this.purchased.loaded = true;
        if (this.purchasedGids.length) {
          const data = await this.gql(NODES_QUERY, {
            ids: this.purchasedGids,
            country: this.country,
            language: this.language,
          });
          this.purchased.queue = ((data && data.nodes) || []).filter(Boolean);
        }
      }
      const out = [];
      while (out.length < want && this.purchased.queue.length) {
        const p = this.purchased.queue.shift();
        if (this.accept(p)) out.push(p);
      }
      if (!this.purchased.queue.length) this.exhausted.purchased = true;
      return out;
    }

    async pullQuery(sourceKey, state, q, want, discountFilter) {
      if (state.done && !state.spill.length) {
        this.exhausted[sourceKey] = true;
        return [];
      }
      const out = [];
      const PAGE_CAP = 8;
      let pages = 0;
      while (out.length < want && !state.done && pages < PAGE_CAP) {
        pages++;
        const data = await this.gql(FEED_QUERY, {
          first: 40,
          after: state.cursor,
          q,
          country: this.country,
          language: this.language,
        });
        const conn = data && data.products;
        if (!conn || !conn.edges.length) {
          state.done = true;
          break;
        }
        let reachedWant = false;
        for (const edge of conn.edges) {
          state.cursor = edge.cursor;
          const p = edge.node;
          if (discountFilter && this.isDiscounted(p) !== !!this.config.isDiscounted) {
            state.spill.push(p);
            continue;
          }
          if (this.accept(p)) {
            out.push(p);
            if (out.length >= want) {
              reachedWant = true;
              break;
            }
          }
        }
        if (reachedWant) break;
        if (!conn.pageInfo.hasNextPage) {
          state.done = true;
          break;
        }
      }
      // Matching the PDP's discount state is a preference, not a requirement:
      // once the source runs out of pages, fall back to the products it set
      // aside so the feed doesn't starve on a catalog that skews one way.
      while (state.done && out.length < want && state.spill.length) {
        const p = state.spill.shift();
        if (this.accept(p)) out.push(p);
      }
      if (state.done && !state.spill.length) this.exhausted[sourceKey] = true;
      return out;
    }

    // --- loading / rendering -------------------------------------------------
    async loadNext() {
      if (this.loading || this.done) return;
      this.loading = true;
      this.classList.add('is-loading');
      try {
        const products = await this.fillBatch(this.batchSize);
        if (products.length) {
          this.renderBatch(products);
          this.emptyRetries = 0;
        }
        if (this.srcIndex >= this.sources.length) {
          this.finish();
        } else if (!products.length) {
          this.emptyRetries += 1;
          if (this.emptyRetries >= 4) this.finish();
        }
      } catch (e) {
        console.warn('[recs] load error', e);
      } finally {
        this.loading = false;
        this.classList.remove('is-loading');
        if (!this.done) this.maybeContinue();
      }
    }

    // Content shorter than the viewport won't re-trigger the observer, so keep
    // loading while the sentinel is still near the fold.
    maybeContinue() {
      if (!this.sentinel) return;
      const rect = this.sentinel.getBoundingClientRect();
      if (rect.top < window.innerHeight + 200) {
        requestAnimationFrame(() => this.loadNext());
      }
    }

    renderBatch(products) {
      const html = products.map((p) => this.cardHTML(p)).join('');
      this.grid.insertAdjacentHTML('beforeend', html);

      // Mark hearts for already-favourited products. wishlist.js's own load flow
      // also re-scans once its data is ready, covering the logged-in async case.
      if (window.wishlist && typeof window.wishlist.updateAllButtons === 'function') {
        window.wishlist.updateAllButtons();
      }
      document.dispatchEvent(new CustomEvent('recs:rendered', { detail: { count: products.length } }));
    }

    finish() {
      this.done = true;
      if (this.observer) this.observer.disconnect();
      this.classList.remove('is-loading');
      if (this.status) this.status.style.display = 'none';
      if (this.sentinel) this.sentinel.style.display = 'none';
      if (this.grid && !this.grid.children.length) this.style.display = 'none';
    }

    aspectRatio(image) {
      const r = this.config.ratio;
      if (r === 'portrait') return 'var(--aspect-ratio-portait)';
      if (r === 'square') return '1';
      if (r === 'adapt' && image && image.width && image.height) return `${image.width} / ${image.height}`;
      return 'auto';
    }

    money(amount, currency) {
      const lang = document.documentElement.lang || 'es-ES';
      try {
        return new Intl.NumberFormat(lang, { style: 'currency', currency: currency || 'EUR' }).format(parseFloat(amount || '0'));
      } catch {
        return `${parseFloat(amount || '0').toFixed(2)} ${currency || ''}`.trim();
      }
    }

    cardHTML(p) {
      const id = numericId(p.id);
      const handle = p.handle;
      const title = esc(p.title);
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = `${root}products/${handle}`;

      const currency = p.priceRange?.minVariantPrice?.currencyCode || window.currency || 'EUR';
      const priceAmount = p.priceRange?.minVariantPrice?.amount || '0';
      const formattedPrice = this.money(priceAmount, currency);

      const lowStockThreshold = window.lowStockThreshold ?? 3;
      const variants = (p.variants?.nodes || []).map((v) => ({
        id: numericId(v.id),
        title: v.title,
        available: !!v.availableForSale,
        size: sizeOf(v),
        lowStock:
          !!v.availableForSale &&
          Number.isFinite(v.quantityAvailable) &&
          v.quantityAvailable > 0 &&
          v.quantityAvailable <= lowStockThreshold,
      }));
      const singleProduct = variants.length <= 1;
      const firstVariant = variants.find((v) => v.available) || variants[0] || { id: '' };
      const firstVariantId = firstVariant.id;

      const image = p.featuredImage;
      const alt = esc(image?.altText || p.title);
      const ratio = this.aspectRatio(image);

      const fav =
        !!window.wishlist &&
        typeof window.wishlist.hasProduct === 'function' &&
        window.wishlist.hasProduct(String(id));

      const variantsForJson = variants.map((v) => ({
        id: `gid://shopify/ProductVariant/${v.id}`,
        title: v.title,
        availableForSale: v.available,
        lowStock: v.lowStock,
        selectedOptions: [{ name: 'size', value: v.size }],
      }));

      let desktopControls;
      if (singleProduct) {
        desktopControls =
          `<div class="add__to__cart add__to__cart--desktop-single" data-product-id="${id}" data-first-variant="${firstVariantId}" data-product-handle="${esc(handle)}" data-single="true" data-prefetched="false">` +
          `<p>${esc(window.variantStrings?.oneSize || 'Talla única')}</p></div>`;
      } else {
        const elems = variants
          .map(
            (v) =>
              `<div class="variant-elem${v.lowStock ? ' variant-elem--low-stock' : ''}" aria-available="${v.available}" data-product-id="${id}" data-variant-id="${v.id}">` +
              `<span class="variant-elem__size">${esc(v.size)}</span>${v.lowStock ? CLOCK_ICON : ''}</div>`
          )
          .join('');
        desktopControls = `<div class="variants-container">${elems}</div>`;
      }

      const imgHtml = image?.url
        ? `<img class="card__img" src="${image.url}&width=1000" srcset="${image.url}&width=375 375w, ${image.url}&width=750 750w, ${image.url}&width=1000 1000w, ${image.url}&width=1440 1440w" sizes="(min-width: 1000px) 620px, 100vw" alt="${alt}" loading="lazy"${image.width ? ` width="${image.width}"` : ''}${image.height ? ` height="${image.height}"` : ''}>`
        : '<div class="card__img card__img--empty"></div>';

      const priceHtml = `<span class="price-item">${formattedPrice}</span>`;

      return (
        `<li class="recs-grid__item collection__grid__item">` +
        `<color-swatch>` +
        `<a class="brn-card" href="${url}" aria-label="${title}" tabindex="0">` +
        `<div class="brn-card__image" data-available="true" data-product-title="${esc(p.title)}">` +
        `<button type="button" class="add__to__cart add__to__cart--tablet" data-product-id="${id}" data-first-variant="${firstVariantId}" data-single="${singleProduct}" data-product-handle="${esc(handle)}" data-coming-soon="false" data-product-image="${image?.url || ''}" data-prefetched="false">${PLUS_ICON}</button>` +
        `<div class="add__to__cart--desktop">${desktopControls}</div>` +
        `<picture class="brn-card__picture brn-card__picture--static" data-uri="${esc(handle)}" style="--picture-aspect-ratio: ${ratio}">${imgHtml}</picture>` +
        `</div>` +
        `<div class="brn-card__footer">` +
        `<div class="brn-card__content">` +
        `<h3 class="brn-card__title">${title}</h3>` +
        `<script type="application/json" id="variants-${id}">${JSON.stringify(variantsForJson)}</script>` +
        `<div class="add__to__cart add__to__cart--mobile" data-product-id="${id}" data-first-variant="${firstVariantId}" data-single="${singleProduct}" data-product-handle="${esc(handle)}" data-coming-soon="false" data-product-image="${image?.url || ''}" data-prefetched="false">${CART_ICON}<p>${esc(window.variantStrings?.addToCart || 'Añadir a la cesta')}</p></div>` +
        `<div class="brn-card__price card__price"><div class="price"><div class="price__container"><div class="price__sale visible"><div class="price__sale__details-wrapper">${priceHtml}</div></div></div></div></div>` +
        `</div>` +
        `<div class="brn-card__actions">` +
        `<button class="btn btn-icon brn-product__fav${fav ? ' active' : ''}" type="button" data-product="${id}" data-variant="${firstVariantId}" data-handle="${esc(handle)}" data-title="${esc(p.title)}" data-image="${image?.url || ''}" aria-pressed="${fav ? 'true' : 'false'}"><span class="sr-only">Like</span>${HEART_ICON}</button>` +
        `</div>` +
        `</div>` +
        `</a></color-swatch></li>`
      );
    }
  }

  if (!customElements.get('recommendations-feed')) {
    customElements.define('recommendations-feed', RecommendationsFeed);
  }
})();
